import { MetricSubsection } from '@/features/projects/components/metricThresholds/MetricSubsection';
import { ThresholdPreviewChips } from '@/features/projects/components/metricThresholds/ThresholdPreviewChips';
import { r, s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { Inline, Stack, Text } from '@/ui';
import { settingsCopyTextSx } from '@/ui/composites/settingsPageTypographySx';
import { Slider, Switch, type Theme } from '@/ui/primitives';
import { darken } from '@/ui/primitives/colorUtils';
import type { ThresholdDirection } from '@/features/projects/components/metricThresholds/utils';
import type { ReactElement } from 'react';

type ThresholdSliderMetricSectionProps = {
  title: string;
  enabled: boolean;
  warn: number;
  target: number;
  formatter: (value: number) => string;
  previewValues: { red: number; yellowMin: number; yellowMax: number; green: number };
  onToggle: (checked: boolean) => void;
  onChange: (warn: number, target: number) => void;
  direction?: ThresholdDirection;
  disabled?: boolean;
};

const sliderStatusColors = {
  red: darken(uiTokens.statusColors.red, 0.16),
  orange: darken(uiTokens.statusColors.orange, 0.14),
  green: darken(uiTokens.statusColors.green, 0.12),
} as const;

const buildSliderGradient = (warn: number, target: number): string => {
  const [start, end] = warn <= target ? [warn, target] : [target, warn];
  return `linear-gradient(90deg, ${sliderStatusColors.red} 0%, ${sliderStatusColors.red} ${start}%, ${sliderStatusColors.orange} ${start}%, ${sliderStatusColors.orange} ${end}%, ${sliderStatusColors.green} ${end}%, ${sliderStatusColors.green} 100%)`;
};

const buildValueLabelSx = (
  theme: Theme,
): Record<string, string | number | undefined | Record<string, string>> => ({
  backgroundColor: 'background.paper',
  color: 'text.primary',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: r('sm'),
  boxShadow: 'none',
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: 400,
  lineHeight: theme.typography.subtitle1.lineHeight,
  padding: `${s('xs')} ${s('sm')}`,
  '&::before': {
    backgroundColor: 'background.paper',
    borderBottom: '1px solid',
    borderRight: '1px solid',
    borderColor: 'divider',
    boxSizing: 'border-box',
  },
});

export const ThresholdSliderMetricSection = ({
  title,
  enabled,
  warn,
  target,
  formatter,
  previewValues,
  onToggle,
  onChange,
  direction = 'higherBetter',
  disabled = false,
}: ThresholdSliderMetricSectionProps): ReactElement => {
  const railBackground = enabled
    ? buildSliderGradient(warn, target)
    : uiTokens.statusColors.neutral;

  return (
    <MetricSubsection
      title={title}
      headerAccessory={
        <Switch
          size="small"
          density="compact"
          checked={enabled}
          disabled={disabled}
          onChange={(_event, checked) => {
            onToggle(checked);
          }}
        />
      }
    >
      <Stack gap="lg">
        <Stack gap="none">
          <Slider
            value={[warn, target]}
            min={0}
            max={100}
            step={1}
            disabled={!enabled}
            valueLabelDisplay="auto"
            valueLabelFormat={formatter}
            disableSwap
            sx={{
              height: s('md'),
              pb: s('sm'),
              '& .MuiSlider-rail': {
                backgroundImage: railBackground,
                opacity: 1,
                height: s('md'),
                border: 'none',
              },
              '& .MuiSlider-track': {
                backgroundColor: 'transparent',
                height: s('md'),
                border: 'none',
              },
              '& .MuiSlider-thumb': {
                width: s('xxs'),
                height: s('xl'),
                backgroundColor: 'common.white',
                border: '1.5px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                borderRadius: r('sm'),
                '&:hover, &.Mui-focusVisible, &.Mui-active': {
                  boxShadow: 'none',
                },
              },
              '& .MuiSlider-valueLabel': (theme: Theme) => buildValueLabelSx(theme),
            }}
            onChange={(_event: Event, value: number | number[], _activeThumb: number) => {
              if (!Array.isArray(value) || value.length < 2) return;
              const [nextWarn, nextTarget] = value;
              if (typeof nextWarn !== 'number' || typeof nextTarget !== 'number') {
                return;
              }
              onChange(nextWarn, nextTarget);
            }}
          />
          <Inline justify="space-between">
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Alert below {warn}%
            </Text>
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              Warn below {target}%
            </Text>
          </Inline>
        </Stack>
        <ThresholdPreviewChips
          values={previewValues}
          formatter={formatter}
          direction={direction}
          warn={warn}
          target={target}
          disabled={!enabled}
        />
      </Stack>
    </MetricSubsection>
  );
};
